import React, { useEffect, useMemo, useState } from 'react';
import {
  formatEsatGrade,
  formatPaperRaw,
  loadAllEsatChosenOptionalKeys,
  loadEsatPaperScores,
  loadEsatTarget,
  resetEsatSittingProgress,
  saveEsatPaperScore,
  saveEsatTarget,
  sittingScoreFromEsatModules,
} from '../utils/esatScore';
import useWorkspacePaperProgress, { useUserTarget } from '../hooks/useWorkspacePaperProgress';
import { ESAT_OPTIONAL_KEYS } from '../utils/classifyEsatModule';
import { normalizeEsatHeritageExam } from '../utils/esatModulePicker';
import {
  esatFurtherColumnChips,
  esatOptionalChipsAriaLabel,
  esatSittingTableHeaders,
  esatWorkspaceRowStatus,
  resolveEsatAttemptModules,
} from '../utils/esatHeritageSelection';
import { collectTopicPaperIds, topicAttemptRowFromMap, topicAttemptStatusRequestKey, topicRowAction } from '../utils/topicAttemptRow';
import { firstPopulatedMockTier } from '../utils/firstPopulatedMockTier';
import { useAuth } from '../../auth/hooks/useAuth';
import PracticeTopicsNav from './PracticeTopicsNav';
import PracticeTopicsPages from './PracticeTopicsPages';
import { topicPapersContent } from './TopicPapersBlock';
import {
  SUBTOPIC_PAPERS_LABEL,
  SUBTOPIC_PAPERS_ALL_PAGE,
  SUBTOPIC_TIER_PAGES,
  SUBTOPIC_PAGE_TITLES,
  subtopicPapersOfTier,
} from '../utils/subtopicPapers';
import PracticeTopicsOverview from './PracticeTopicsOverview';
import useSectionPaperTierProgress from '../hooks/useSectionPaperTierProgress';
import { PRACTICE_TOPIC_PAGE_TITLES, practiceProgressSummary } from '../utils/practiceTopics';
import './EsatWorkspace.css';

const PAGES = {
  overview: 'Overview',
  ...PRACTICE_TOPIC_PAGE_TITLES,
  'mock-all': 'Mock Tests',
  'mock-beginnings': 'Mock Tests: Beginnings',
  'mock-foundations': 'Mock Tests: Foundations',
  'mock-beyond': 'Mock Tests: Beyond',
  'past-nsaa': 'Past Papers: NSAA',
  'past-engaa': 'Past Papers: ENGAA',
};

const TARGET_OPTIONS = [
  { value: 6.5, label: 'University of Cambridge — Engineering (MEng)' },
  { value: 6.5, label: 'University of Cambridge — Natural Sciences (BA/MSci)' },
  { value: 6.2, label: 'Imperial College London — Chemical / Aerospace Engineering' },
  { value: 6.0, label: 'Imperial College London — Design Engineering & Physics' },
  { value: 6.5, label: 'University of Oxford — Engineering Science (MEng)' },
  { value: 6.0, label: 'University College London (UCL) — Electronic & Electrical Eng' },
  { value: 5.8, label: "King's College London (KCL) — General Engineering (BEng/MEng)" },
];

function moduleIdsForSitting(sitting) {
  const ids = [{ key: 'mathematics_1', id: sitting?.mathematics1?.id || sitting?.paper1?.id }];
  ESAT_OPTIONAL_KEYS.forEach((key) => {
    const id = sitting?.optionals?.[key]?.id;
    if (id) ids.push({ key, id });
  });
  return ids;
}

function paperIds(sitting) {
  return moduleIdsForSitting(sitting).map((m) => m.id).filter(Boolean);
}

function sittingLabel(sitting, index, kind) {
  if (kind === 'past') {
    const year = String(sitting?.title || '').match(/\b(20\d{2})\b/);
    return year ? year[1] : sitting?.title || `Paper ${index + 1}`;
  }
  return `Test ${index + 1}`;
}

function paperHeritagePrefix(item) {
  const hay = [item?.id, item?.catalog_id, item?.startId, item?.display_name, item?.title, item?.heritageExam]
    .map((v) => String(v || '').toUpperCase())
    .join(' ');
  if (hay.includes('ENGAA')) return 'ENGAA';
  if (hay.includes('NSAA')) return 'NSAA';
  if (hay.includes('BMAT')) return 'BMAT';
  if (hay.includes('TSA')) return 'TSA';
  if (hay.includes('TARA')) return 'TARA';
  if (hay.includes('ESAT')) return 'ESAT';
  return null;
}

function StatusPill({ status }) {
  const map = {
    done: ['completed', 'Completed'],
    progress: ['in-progress', 'In progress'],
    none: ['not-started', 'Not started'],
  };
  const [cls, label] = map[status] || map.none;
  return <span className={`esat-ws-pill ${cls}`}>{label}</span>;
}

function ModeCell({ mode, status }) {
  if (status === 'none' || !mode) return '—';
  return <span className="esat-ws-mode">{mode === 'timed' ? 'Timed' : 'Untimed'}</span>;
}

function avgGrades(grades) {
  const nums = grades.filter((g) => g != null && Number.isFinite(Number(g))).map(Number);
  if (!nums.length) return null;
  return Math.round((nums.reduce((a, b) => a + b, 0) / nums.length) * 10) / 10;
}

function storedKeysForSitting(sitting, chosenMap) {
  const fromMap = chosenMap?.[sitting?.startId]?.keys;
  if (Array.isArray(fromMap) && fromMap.length) return fromMap;
  return [];
}

function enrichSittings(sittings, scoresMap, dbPapers = {}, chosenMap = {}) {
  return (sittings || [])
    .filter((s) => s.kind === 'sitting')
    .map((sitting, index) => {
      const storedOptionalKeys = storedKeysForSitting(sitting, chosenMap);
      const { sat, moduleIds } = resolveEsatAttemptModules({
        sitting,
        scoresMap,
        dbPapers,
        storedOptionalKeys,
      });
      const scored = sittingScoreFromEsatModules(sat ? moduleIds : [], scoresMap);
      const status = esatWorkspaceRowStatus({
        sat,
        moduleIds: sat ? moduleIds : [],
        scoresMap,
        dbPapers,
      });
      return { ...sitting, index, scored, status, attemptSat: sat, attemptModuleIds: moduleIds };
    });
}

function tierStats(rows) {
  const done = rows.filter((r) => r.status === 'done').length;
  const grades = rows.map((r) => r.scored.grade);
  return {
    done,
    total: rows.length,
    avg: avgGrades(grades),
    pct: rows.length ? Math.round((done / rows.length) * 100) : 0,
  };
}

const EsatWorkspace = ({
  onBack,
  mockSittings = [],
  pastSittings = [],
  sectionPapers = [],
  subtopicPapers = [],
  topicSections = [],
  isPartUnlocked,
  onStartSetPaper,
  onStartTopicPaper,
  topicStatus = 'ready',
  onContinueSitting,
  onReviewSitting,
  onRequestUpgrade,
}) => {
  const { user } = useAuth();
  const userId = user?.id || null;
  const [page, setPage] = useState('overview');
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scoreTick, setScoreTick] = useState(0);
  const [toast, setToast] = useState(null);
  const [target, setTarget] = useUserTarget(loadEsatTarget, saveEsatTarget, userId);

  useEffect(() => {
    document.body.classList.add('exam-workspace-open');
    return () => document.body.classList.remove('exam-workspace-open');
  }, []);

  const statusRequestKey = topicAttemptStatusRequestKey([
    ...[...mockSittings, ...pastSittings].flatMap((s) => {
      if (s?.kind === 'sitting') return moduleIdsForSitting(s).map((m) => m.id).filter(Boolean);
      return s?.id ? [s.id] : [];
    }),
    ...collectTopicPaperIds(topicSections),
  ]);

  const {
    dbPapers,
    scoresMap,
    completedList,
  } = useWorkspacePaperProgress('ESAT', statusRequestKey, {
    userId,
    loadScores: loadEsatPaperScores,
    saveScore: saveEsatPaperScore,
    cacheTick: scoreTick,
  });



  const chosenMap = useMemo(() => {
    void scoreTick;
    return loadAllEsatChosenOptionalKeys(userId);
  }, [scoreTick, userId]);

  const beginnings = useMemo(
    () => enrichSittings(
      mockSittings.filter((s) => (s.tier || s.mathematics1?.tier || s.paper1?.tier) === 'beginnings'),
      scoresMap,
      dbPapers,
      chosenMap
    ),
    [mockSittings, scoresMap, dbPapers, chosenMap]
  );
  const foundations = useMemo(
    () => enrichSittings(
      mockSittings.filter((s) => (s.tier || s.mathematics1?.tier || s.paper1?.tier) === 'foundations'),
      scoresMap,
      dbPapers,
      chosenMap
    ),
    [mockSittings, scoresMap, dbPapers, chosenMap]
  );
  const beyond = useMemo(
    () => enrichSittings(
      mockSittings.filter((s) => (s.tier || s.mathematics1?.tier || s.paper1?.tier) === 'beyond'),
      scoresMap,
      dbPapers,
      chosenMap
    ),
    [mockSittings, scoresMap, dbPapers, chosenMap]
  );

  const mocksGoto = useMemo(
    () => firstPopulatedMockTier(beginnings, foundations, beyond),
    [beginnings, foundations, beyond]
  );
  const pastNsaa = useMemo(
    () => enrichSittings(
      pastSittings.filter((s) => s.kind === 'sitting' && paperHeritagePrefix(s) === 'NSAA'),
      scoresMap,
      dbPapers,
      chosenMap
    ).sort((a, b) => String(a.title || '').localeCompare(String(b.title || ''), undefined, { numeric: true })),
    [pastSittings, scoresMap, dbPapers, chosenMap]
  );
  const pastEngaa = useMemo(
    () => enrichSittings(
      pastSittings.filter((s) => s.kind === 'sitting' && paperHeritagePrefix(s) === 'ENGAA'),
      scoresMap,
      dbPapers,
      chosenMap
    ).sort((a, b) => String(a.title || '').localeCompare(String(b.title || ''), undefined, { numeric: true })),
    [pastSittings, scoresMap, dbPapers, chosenMap]
  );
  const past = useMemo(() => [...pastNsaa, ...pastEngaa], [pastNsaa, pastEngaa]);
  const topicCount = useMemo(
    () => (topicSections || []).reduce(
      (sum, sec) => sum + (sec.tiers || []).reduce((n, t) => n + (t.params?.length || 0), 0),
      0
    ),
    [topicSections]
  );
  const topicsDone = useMemo(
    () => (topicSections || []).reduce(
      (sum, sec) => sum + (sec.tiers || []).reduce(
        (n, t) => n + (t.params || []).filter((p) => completedList.includes(p.paper_id)).length,
        0
      ),
      0
    ),
    [topicSections, completedList]
  );

  // Section papers are listed under Practice Topics (Foundations and Beyond), so the
  // Overview's Practice Topics rows count them with the topics.
  const sectionProgress = useSectionPaperTierProgress('ESAT', sectionPapers, completedList);
  const practiceTopicsRow = practiceProgressSummary({ topicsDone, topicCount, papers: sectionProgress.all });

  const overview = useMemo(() => {
    const allMocks = [...beginnings, ...foundations, ...beyond];
    const all = [...allMocks, ...past];
    const graded = all.filter((r) => r.scored.grade != null);
    const currentAvg = avgGrades(graded.map((r) => r.scored.grade));

    let totalQuestions = 0;
    let attempted = 0;
    let correct = 0;
    all.forEach((row) => {
      totalQuestions += row.questionCount || 0;
      Object.values(row.scored.modules || {}).forEach((m) => {
        if (!m) return;
        attempted += Number(m.total) || 0;
        correct += Number(m.correct) || 0;
      });
    });

    const timed = graded.filter((r) => r.scored.mode === 'timed');
    const untimed = graded.filter((r) => r.scored.mode === 'untimed');
    const mocksDone = allMocks.filter((r) => r.status === 'done').length;
    const pastDone = past.filter((r) => r.status === 'done').length;

    return {
      currentAvg,
      totalQuestions,
      attempted,
      successRate: attempted > 0 ? Math.round((correct / attempted) * 100) : null,
      scoredAttempts: graded.length,
      beginnings: tierStats(beginnings),
      foundations: tierStats(foundations),
      beyond: tierStats(beyond),
      nsaa: tierStats(pastNsaa),
      engaa: tierStats(pastEngaa),
      mocks: {
        done: mocksDone,
        total: allMocks.length,
        avg: avgGrades(allMocks.map((r) => r.scored.grade)),
        pct: allMocks.length ? Math.round((mocksDone / allMocks.length) * 100) : 0,
      },
      past: {
        done: pastDone,
        total: past.length,
        avg: avgGrades(past.map((r) => r.scored.grade)),
        pct: past.length ? Math.round((pastDone / past.length) * 100) : 0,
      },
      timed: {
        count: timed.length,
        total: graded.length,
        avg: avgGrades(timed.map((r) => r.scored.grade)),
        pct: graded.length ? Math.round((timed.length / graded.length) * 100) : 0,
      },
      untimed: {
        count: untimed.length,
        total: graded.length,
        avg: avgGrades(untimed.map((r) => r.scored.grade)),
        pct: graded.length ? Math.round((untimed.length / graded.length) * 100) : 0,
      },
    };
  }, [beginnings, foundations, beyond, past, pastNsaa, pastEngaa]);

  const showToast = (msg) => {
    setToast(msg);
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(''), 2600);
  };

  const goTo = (id) => {
    setPage(id);
    setMobileOpen(false);
  };

  const startSitting = (sitting, options = {}) => {
    const paper = sitting.mathematics1 || sitting.paper1;
    if (!paper) return;
    const unlocked = isPartUnlocked ? isPartUnlocked(paper) : true;
    if (!unlocked) {
      if (onRequestUpgrade) onRequestUpgrade(paper);
      return;
    }
    onStartSetPaper(
      'ESAT',
      sitting.startId,
      sitting.startCatalogId,
      {
        pearsonSitting: true,
        esatOptionals: sitting.optionals,
        mathematics1: sitting.mathematics1 || sitting.paper1,
        heritageExam: normalizeEsatHeritageExam(sitting.heritageExam),
        year: sitting.year || (String(sitting.title || '').match(/\b(20\d{2})\b/) || [])[1] || null,
      },
      options
    );
  };

  const handleRetake = (sitting) => {
    resetEsatSittingProgress(userId, paperIds(sitting), sitting.startId);
    setScoreTick((n) => n + 1);
    startSitting(sitting, { forceNew: true });
  };

  const handleContinue = (sitting) => {
    if (typeof onContinueSitting === 'function' && sitting) {
      onContinueSitting(sitting);
      return;
    }
    showToast('Resume isn’t available yet — use Start or Retake for a new attempt.');
  };

  const handleReview = (sitting) => {
    if (typeof onReviewSitting === 'function' && sitting) {
      onReviewSitting(sitting);
      return;
    }
    showToast('Review isn’t available yet.');
  };

  const targetValue = Number(target?.value) || 0;
  const avg = overview.currentAvg;
  const fillPct = avg != null ? Math.min(100, (avg / 9) * 100) : 0;
  const markerPct = Math.min(100, (targetValue / 9) * 100);
  const diff = avg != null ? Math.round((avg - targetValue) * 10) / 10 : null;
  const markerLabel = (target?.label || 'Target').split(' — ')[0] || 'Target';

  const selectValue = target?.custom
    ? 'custom'
    : TARGET_OPTIONS.find((o) => o.label === target?.label)?.label || TARGET_OPTIONS[5].label;
  const selectedTrack = TARGET_OPTIONS.find((o) => o.label === selectValue);
  const selectDisplay = selectValue === 'custom'
    ? 'Custom target…'
    : selectedTrack
      ? `${selectedTrack.label} (${selectedTrack.value.toFixed(1)}+/9.0)`
      : selectValue;

  const onTargetChange = (e) => {
    const val = e.target.value;
    if (val === 'custom') {
      const next = { value: Number(target.value) || 6, label: 'Custom', custom: true };
      setTarget(next);
      return;
    }
    const opt = TARGET_OPTIONS.find((o) => o.label === val);
    if (!opt) return;
    const next = { value: opt.value, label: opt.label, custom: false };
    setTarget(next);
  };

  const onCustomInput = (e) => {
    const value = Number(e.target.value) || 0;
    const next = { value, label: 'Custom', custom: true };
    setTarget(next);
  };

  const renderTopicsTable = (params) => {
    if (!params?.length) return null;
    return (
      <table className="esat-ws-table">
        <thead>
          <tr>
            <th>Topic</th>
            <th>Questions</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {params.map((param) => {
            const done = completedList.includes(param.paper_id);
            const action = topicRowAction(topicAttemptRowFromMap(dbPapers, param.paper_id), done);
            const status = action.status;
            const title = param.title || param.paper_id;
            return (
              <tr key={param.paper_id}>
                <td data-label="Topic" className="esat-ws-cell-strong">{title}</td>
                <td data-label="Questions" className="esat-ws-cell-mono">
                  {param.question_count != null ? param.question_count : '—'}
                </td>
                <td data-label="Status">
                  <StatusPill status={status} />
                </td>
                <td data-label="">
                  <button
                    type="button"
                    className={action.resume ? 'esat-ws-start-btn mid' : 'esat-ws-start-btn'}
                    onClick={() => onStartTopicPaper?.('ESAT', param, {
                      forceNew: action.forceNew,
                      resume: action.resume,
                    })}
                  >
                    {action.label}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const renderTable = (rows, kind, heritageExam = null) => {
    const headers = esatSittingTableHeaders(heritageExam);
    if (!rows.length) {
      return (
        <div className="esat-ws-empty">
          <p>No past papers in this set yet.</p>
        </div>
      );
    }
    return (
    <table className="esat-ws-table">
      <thead>
        <tr>
          <th>{kind === 'past' ? 'Paper' : 'Test'}</th>
          <th>{headers.first}</th>
          <th>{headers.second}</th>
          <th>Score</th>
          <th>Mode</th>
          <th>Status</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const paper = row.mathematics1 || row.paper1;
          const unlocked = isPartUnlocked ? isPartUnlocked(paper || row) : true;
          const { scored, status } = row;
          const maths1Score = scored.modules?.mathematics_1;
          return (
            <tr key={row.startId || row.title}>
              <td data-label={kind === 'past' ? 'Paper' : 'Test'} className="esat-ws-cell-strong">
                {sittingLabel(row, row.index, kind)}
                {!unlocked && (
                  <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--esat-ink-faint)' }}>Locked</span>
                )}
              </td>
              <td data-label={headers.first} className="esat-ws-cell-mono">
                {formatPaperRaw(maths1Score?.correct, maths1Score?.total)}
              </td>
              <td data-label={headers.second} aria-label={esatOptionalChipsAriaLabel(
                row.attemptSat
                  ? esatFurtherColumnChips({
                    sitting: row,
                    moduleIds: row.attemptModuleIds,
                    scoresMap,
                    heritageExam,
                  })
                  : [],
                row.attemptSat
              )}>
                {row.attemptSat ? (
                  <div className="esat-ws-module-chips">
                    {esatFurtherColumnChips({
                      sitting: row,
                      moduleIds: row.attemptModuleIds,
                      scoresMap,
                      heritageExam,
                    }).map((chip) => (
                      <span key={chip.key} className="esat-ws-mode">
                        {chip.label} {chip.scoreText}
                      </span>
                    ))}
                  </div>
                ) : '—'}
              </td>
              <td data-label="Score" className="esat-ws-cell-mono">
                {formatEsatGrade(scored.grade)}
              </td>
              <td data-label="Mode">
                <ModeCell mode={scored.mode} status={status} />
              </td>
              <td data-label="Status">
                <StatusPill status={status} />
              </td>
              <td data-label="">
                {status === 'done' ? (
                  <div className="esat-ws-actions">
                    <button type="button" className="esat-ws-start-btn ghost" onClick={() => handleReview(row)}>
                      Review
                    </button>
                    <button
                      type="button"
                      className="esat-ws-start-btn ghost"
                      onClick={() => (unlocked ? handleRetake(row) : onRequestUpgrade?.(paper))}
                    >
                      Retake
                    </button>
                  </div>
                ) : status === 'progress' ? (
                  <div className="esat-ws-actions">
                    <button type="button" className="esat-ws-start-btn mid" onClick={() => handleContinue(row)}>
                      Continue
                    </button>
                    <button
                      type="button"
                      className="esat-ws-start-btn ghost"
                      onClick={() => (unlocked ? handleRetake(row) : onRequestUpgrade?.(paper))}
                    >
                      Retake
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="esat-ws-start-btn"
                    onClick={() => {
                      if (!unlocked) {
                        onRequestUpgrade?.(paper);
                        return;
                      }
                      startSitting(row);
                    }}
                  >
                    Start
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
    );
  };

  const BreakdownRow = ({ name, meta, score, pct, goto, staticRow }) => {
    const inner = (
      <>
        <div className="esat-ws-breakdown-name">{name}</div>
        <div className="esat-ws-breakdown-bar-wrap">
          <div className="esat-ws-breakdown-bar">
            <div className="esat-ws-breakdown-bar-fill" style={{ width: `${pct || 0}%` }} />
          </div>
          <div className="esat-ws-breakdown-meta">{meta}</div>
        </div>
        <div className="esat-ws-breakdown-score">{score}</div>
        {!staticRow && <div className="esat-ws-breakdown-arrow">›</div>}
      </>
    );
    if (staticRow) {
      return <div className="esat-ws-breakdown-row static">{inner}</div>;
    }
    return (
      <button type="button" className="esat-ws-breakdown-row" onClick={() => goTo(goto)}>
        {inner}
      </button>
    );
  };

  return (
    <div className="esat-ws">
      <aside className={`esat-ws-sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="esat-ws-sidebar-head">
          <span className="esat-ws-name">ESAT Workspace</span>
          <button
            type="button"
            className="esat-ws-icon-btn"
            title="Collapse sidebar"
            onClick={() => (mobileOpen ? setMobileOpen(false) : setCollapsed((c) => !c))}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
              <line x1="9" y1="4" x2="9" y2="20" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
        </div>

        <nav className="esat-ws-nav">
          <button
            type="button"
            className={`esat-ws-nav-item ${page === 'overview' ? 'active' : ''}`}
            onClick={() => goTo('overview')}
          >
            <svg className="esat-ws-nav-icon" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="7" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
              <rect x="14" y="3" width="7" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
              <rect x="14" y="12" width="7" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
              <rect x="3" y="16" width="7" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
            </svg>
            <span className="esat-ws-nav-label">Overview</span>
          </button>

          <PracticeTopicsNav prefix="esat-ws" page={page} onGo={goTo} />
          <PracticeTopicsNav
            prefix="esat-ws"
            page={page}
            onGo={goTo}
            label={SUBTOPIC_PAPERS_LABEL}
            pages={SUBTOPIC_TIER_PAGES}
          />

          <div className="esat-ws-nav-group">
            <div className="esat-ws-nav-item esat-ws-nav-parent">
              <svg className="esat-ws-nav-icon" viewBox="0 0 24 24" fill="none">
                <path d="M12 3l2.6 5.6 6.1.6-4.6 4.1 1.3 6-5.4-3.1-5.4 3.1 1.3-6-4.6-4.1 6.1-.6L12 3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
              </svg>
              <span className="esat-ws-nav-label">Mock Tests</span>
            </div>
            <div className="esat-ws-nav-sub">
              {[
                ['mock-beginnings', 'Beginnings', 1],
                ['mock-foundations', 'Foundations', 2],
                ['mock-beyond', 'Beyond', 3],
              ].map(([id, label, tier]) => (
                <button
                  key={id}
                  type="button"
                  className={`esat-ws-nav-subitem ${page === id ? 'active' : ''}`}
                  onClick={() => goTo(id)}
                >
                  <svg className="esat-ws-nav-icon" viewBox="0 0 24 24" fill="none">
                    <rect x="4" y="14" width="4" height="7" rx="1" fill="currentColor" opacity={tier >= 1 ? 1 : 0.35} />
                    <rect x="10" y="10" width="4" height="11" rx="1" fill="currentColor" opacity={tier >= 2 ? 1 : 0.35} />
                    <rect x="16" y="5" width="4" height="16" rx="1" fill="currentColor" opacity={tier >= 3 ? 1 : 0.35} />
                  </svg>
                  <span className="esat-ws-nav-label">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {[
            ['mock-beginnings', 'Beginnings', 1],
            ['mock-foundations', 'Foundations', 2],
            ['mock-beyond', 'Beyond', 3],
            ['past-nsaa', 'NSAA', 1],
            ['past-engaa', 'ENGAA', 2],
          ].map(([id, label, tier]) => (
            <button
              key={`c-${id}`}
              type="button"
              className={`esat-ws-nav-item esat-ws-collapsed-tier ${page === id ? 'active' : ''}`}
              title={label}
              onClick={() => goTo(id)}
            >
              <svg className="esat-ws-nav-icon" viewBox="0 0 24 24" fill="none">
                <rect x="4" y="14" width="4" height="7" rx="1" fill="currentColor" opacity={tier >= 1 ? 1 : 0.35} />
                <rect x="10" y="10" width="4" height="11" rx="1" fill="currentColor" opacity={tier >= 2 ? 1 : 0.35} />
                <rect x="16" y="5" width="4" height="16" rx="1" fill="currentColor" opacity={tier >= 3 ? 1 : 0.35} />
              </svg>
              <span className="esat-ws-nav-label">{label}</span>
            </button>
          ))}

          <div className="esat-ws-nav-group">
            <div className="esat-ws-nav-item esat-ws-nav-parent">
              <svg className="esat-ws-nav-icon" viewBox="0 0 24 24" fill="none">
                <path d="M4 19.5V6a2 2 0 012-2h10.5a1.5 1.5 0 011.5 1.5V19a1 1 0 01-1 1H6.5A2.5 2.5 0 014 19.5z" stroke="currentColor" strokeWidth="1.6" />
                <line x1="8" y1="8" x2="14" y2="8" stroke="currentColor" strokeWidth="1.6" />
              </svg>
              <span className="esat-ws-nav-label">Past Papers</span>
            </div>
            <div className="esat-ws-nav-sub">
              {[
                ['past-nsaa', 'NSAA', 1],
                ['past-engaa', 'ENGAA', 2],
              ].map(([id, label, tier]) => (
                <button
                  key={id}
                  type="button"
                  className={`esat-ws-nav-subitem ${page === id ? 'active' : ''}`}
                  onClick={() => goTo(id)}
                >
                  <svg className="esat-ws-nav-icon" viewBox="0 0 24 24" fill="none">
                    <rect x="4" y="14" width="4" height="7" rx="1" fill="currentColor" opacity={tier >= 1 ? 1 : 0.35} />
                    <rect x="10" y="10" width="4" height="11" rx="1" fill="currentColor" opacity={tier >= 2 ? 1 : 0.35} />
                    <rect x="16" y="5" width="4" height="16" rx="1" fill="currentColor" opacity={tier >= 3 ? 1 : 0.35} />
                  </svg>
                  <span className="esat-ws-nav-label">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </nav>
      </aside>

      <main className="esat-ws-main">
        <div className="esat-ws-topbar-mobile">
          <button type="button" className="esat-ws-icon-btn" onClick={() => setMobileOpen((o) => !o)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <line x1="3" y1="6" x2="21" y2="6" stroke="currentColor" strokeWidth="2" />
              <line x1="3" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="2" />
              <line x1="3" y1="18" x2="21" y2="18" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
          <span>{PAGES[page]}</span>
        </div>

        <div className="esat-ws-back-row">
          <button type="button" className="esat-ws-icon-btn esat-ws-back" title="Back" onClick={onBack}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        {page === 'overview' && (
          <section className="esat-ws-page">
            <div className="esat-ws-page-header">
              <h1 className="esat-ws-page-title">Overview</h1>
            </div>

            <div className="esat-ws-target-card">
              <div className="esat-ws-target-field esat-ws-target-field--university">
                <label htmlFor="esat-track">Target university</label>
                <div className="esat-ws-target-select-shell">
                  <span className="esat-ws-target-select-text" aria-hidden="true">
                    {selectDisplay}
                  </span>
                  <select
                    id="esat-track"
                    className="esat-ws-target-select"
                    value={selectValue}
                    onChange={onTargetChange}
                  >
                    {TARGET_OPTIONS.map((o) => (
                      <option key={o.label} value={o.label}>
                        {o.label} ({o.value.toFixed(1)}+/9.0)
                      </option>
                    ))}
                    <option value="custom">Custom target…</option>
                  </select>
                </div>
              </div>
              <div className="esat-ws-target-field">
                <label htmlFor="esat-custom">Custom score</label>
                <input
                  id="esat-custom"
                  type="number"
                  className="esat-ws-target-input"
                  min={1}
                  max={9}
                  step={0.1}
                  placeholder="/ 9.0"
                  hidden={!target.custom}
                  value={target.custom ? target.value : ''}
                  onChange={onCustomInput}
                />
              </div>
              <div className="esat-ws-target-readout">
                <span className="num">
                  {targetValue.toFixed(1)}
                  <span style={{ color: 'var(--esat-ink-faint)', fontWeight: 500 }}>/9.0</span>
                </span>
                <div className="lbl">ESAT module score target</div>
                <div style={{ fontSize: 11, color: 'var(--esat-ink-faint)', marginTop: 2 }}>
                  Per module — Mathematics 1 plus your two course modules
                </div>
              </div>
            </div>

            <div className="esat-ws-section">
              <div className="esat-ws-progress-status" style={{ marginBottom: 8 }}>
                {avg == null ? (
                  'No scored sittings yet — complete a mock or past paper to see progress.'
                ) : diff >= 0 ? (
                  <>
                    <b>{diff.toFixed(1)} points</b> above target — keep it up
                  </>
                ) : (
                  <>
                    <b>{Math.abs(diff).toFixed(1)} points</b> below target
                  </>
                )}
              </div>
              <div className="esat-ws-progress-track">
                <div className="esat-ws-progress-fill" style={{ width: `${fillPct}%` }} />
                <div
                  className="esat-ws-progress-marker"
                  data-label={markerLabel}
                  style={{ left: `${markerPct}%` }}
                />
              </div>
              <div className="esat-ws-breakdown-meta">
                Current average:{' '}
                <b style={{ color: 'var(--esat-ink)', fontFamily: 'var(--esat-mono)' }}>
                  {formatEsatGrade(avg)}
                </b>
                {overview.scoredAttempts > 0
                  ? ` · based on ${overview.scoredAttempts} scored sitting${overview.scoredAttempts === 1 ? '' : 's'}`
                  : ''}
                {overview.scoredAttempts > 0 ? ' · mean of modules sat' : ''}
              </div>
            </div>

            <div className="esat-ws-stat-strip">
              <div className="esat-ws-stat-cell">
                <span className="esat-ws-stat-value">{overview.totalQuestions.toLocaleString()}</span>
                <span className="esat-ws-stat-label">Total questions</span>
              </div>
              <div className="esat-ws-stat-cell">
                <span className="esat-ws-stat-value">{overview.attempted.toLocaleString()}</span>
                <span className="esat-ws-stat-label">Questions attempted</span>
              </div>
              <div className="esat-ws-stat-cell">
                <span className="esat-ws-stat-value">
                  {overview.successRate != null ? `${overview.successRate}%` : '—'}
                </span>
                <span className="esat-ws-stat-label">Success rate</span>
              </div>
              <div className="esat-ws-stat-cell">
                <span className="esat-ws-stat-value">{formatEsatGrade(avg)}</span>
                <span className="esat-ws-stat-label">Average score</span>
              </div>
            </div>

            <div className="esat-ws-section">
              <h2 className="esat-ws-section-title">By tier</h2>
              <BreakdownRow
                name="Beginnings"
                meta={`${overview.beginnings.done} of ${overview.beginnings.total} mocks completed`}
                score={formatEsatGrade(overview.beginnings.avg)}
                pct={overview.beginnings.pct}
                goto="mock-beginnings"
              />
              <BreakdownRow
                name="Foundations"
                meta={`${overview.foundations.done} of ${overview.foundations.total} mocks completed`}
                score={formatEsatGrade(overview.foundations.avg)}
                pct={overview.foundations.pct}
                goto="mock-foundations"
              />
              <BreakdownRow
                name="Beyond"
                meta={`${overview.beyond.done} of ${overview.beyond.total} mocks completed`}
                score={formatEsatGrade(overview.beyond.avg)}
                pct={overview.beyond.pct}
                goto="mock-beyond"
              />
            </div>

            <div className="esat-ws-section">
              <h2 className="esat-ws-section-title">By type</h2>
              <BreakdownRow
                name="Practice Topics"
                meta={practiceTopicsRow.meta}
                score={practiceTopicsRow.score}
                pct={practiceTopicsRow.pct}
                goto="practice"
              />
              <BreakdownRow
                name="Mock Tests"
                meta={`${overview.mocks.done} of ${overview.mocks.total} mocks completed`}
                score={formatEsatGrade(overview.mocks.avg)}
                pct={overview.mocks.pct}
                goto="mock-all"
              />
              <BreakdownRow
                name="NSAA"
                meta={`${overview.nsaa.done} of ${overview.nsaa.total} papers completed`}
                score={formatEsatGrade(overview.nsaa.avg)}
                pct={overview.nsaa.pct}
                goto="past-nsaa"
              />
              <BreakdownRow
                name="ENGAA"
                meta={`${overview.engaa.done} of ${overview.engaa.total} papers completed`}
                score={formatEsatGrade(overview.engaa.avg)}
                pct={overview.engaa.pct}
                goto="past-engaa"
              />
            </div>

            <PracticeTopicsOverview
              prefix="esat-ws"
              topicsDone={topicsDone}
              topicCount={topicCount}
              papers={sectionProgress}
              onGo={goTo}
            />

            <div className="esat-ws-section">
              <h2 className="esat-ws-section-title">By mode</h2>
              <BreakdownRow
                name="Timed"
                meta={
                  overview.scoredAttempts
                    ? `${overview.timed.count} of ${overview.timed.total} attempts`
                    : 'No attempts yet'
                }
                score={formatEsatGrade(overview.timed.avg)}
                pct={overview.timed.pct}
                staticRow
              />
              <BreakdownRow
                name="Untimed"
                meta={
                  overview.scoredAttempts
                    ? `${overview.untimed.count} of ${overview.untimed.total} attempts`
                    : 'No attempts yet'
                }
                score={formatEsatGrade(overview.untimed.avg)}
                pct={overview.untimed.pct}
                staticRow
              />
            </div>
          </section>
        )}

        <PracticeTopicsPages
          page={page}
          prefix="esat-ws"
          exam="ESAT"
          topics={(tier) => (
            topicPapersContent({
              prefix: "esat-ws",
              tier,
              topicSections,
              topicStatus,
              renderTable: renderTopicsTable,
            })
          )}
          sectionPapers={sectionPapers}
          completedList={completedList}
          isPartUnlocked={isPartUnlocked}
          onStartSetPaper={onStartSetPaper}
          onRequestUpgrade={onRequestUpgrade}
        />
        <PracticeTopicsPages
          page={page}
          prefix="esat-ws"
          exam="ESAT"
          label={SUBTOPIC_PAPERS_LABEL}
          allPage={SUBTOPIC_PAPERS_ALL_PAGE}
          tierPages={SUBTOPIC_TIER_PAGES}
          titles={SUBTOPIC_PAGE_TITLES}
          ofTier={subtopicPapersOfTier}
          papers={subtopicPapers}
          completedList={completedList}
          isPartUnlocked={isPartUnlocked}
          onStartSetPaper={onStartSetPaper}
          onRequestUpgrade={onRequestUpgrade}
        />

        {page === 'mock-all' && (
          <section className="esat-ws-page">
            <div className="esat-ws-page-header">
              <h1 className="esat-ws-page-title">Mock Tests</h1>
            </div>
            {[
              ['Beginnings', beginnings],
              ['Foundations', foundations],
              ['Beyond', beyond],
            ].filter(([, rows]) => rows.length).map(([label, rows]) => (
              <div key={label} className="esat-ws-section">
                <h2 className="esat-ws-section-title">{label}</h2>
                {renderTable(rows, 'mock')}
              </div>
            ))}
          </section>
        )}

        {page === 'mock-beginnings' && (
          <section className="esat-ws-page">
            <div className="esat-ws-page-header">
              <h1 className="esat-ws-page-title">Mock Tests: Beginnings</h1>
            </div>
            {renderTable(beginnings, 'mock', null)}
          </section>
        )}

        {page === 'mock-foundations' && (
          <section className="esat-ws-page">
            <div className="esat-ws-page-header">
              <h1 className="esat-ws-page-title">Mock Tests: Foundations</h1>
            </div>
            {renderTable(foundations, 'mock', null)}
          </section>
        )}

        {page === 'mock-beyond' && (
          <section className="esat-ws-page">
            <div className="esat-ws-page-header">
              <h1 className="esat-ws-page-title">Mock Tests: Beyond</h1>
            </div>
            {renderTable(beyond, 'mock', null)}
          </section>
        )}

        {page === 'past-nsaa' && (
          <section className="esat-ws-page">
            <div className="esat-ws-page-header">
              <h1 className="esat-ws-page-title">Past Papers: NSAA</h1>
            </div>
            {renderTable(pastNsaa, 'past', 'NSAA')}
          </section>
        )}

        {page === 'past-engaa' && (
          <section className="esat-ws-page">
            <div className="esat-ws-page-header">
              <h1 className="esat-ws-page-title">Past Papers: ENGAA</h1>
            </div>
            {renderTable(pastEngaa, 'past', 'ENGAA')}
          </section>
        )}
      </main>

      <div className={`esat-ws-toast ${toast ? 'show' : ''}`}>{toast}</div>
    </div>
  );
};

export default EsatWorkspace;
